// Localização: frontend/src/context/SearchContext.js
// Contexto global para busca adaptativa no header

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const SearchContext = createContext();

export const SearchProvider = ({ children }) => {
    const [searchTerm, setSearchTermState] = useState('');
    const [searchPlaceholder, setSearchPlaceholder] = useState('Buscar...');
    const [onSearchChange, setOnSearchChange] = useState(null);
    const location = useLocation();
    const prevPathnameRef = React.useRef(location.pathname);

    // Resetar busca ao mudar de página (apenas quando muda realmente)
    useEffect(() => {
        // Só reseta se a rota realmente mudou
        if (prevPathnameRef.current !== location.pathname) {
            prevPathnameRef.current = location.pathname;
            // Reset apenas quando muda de página, não durante a digitação
            setSearchTermState('');
        }
    }, [location.pathname]);

    // Função para atualizar searchTerm (garante que sempre seja uma string)
    const setSearchTerm = useCallback((value) => {
        // Converte para string, tratando null/undefined como string vazia
        const newValue = value == null ? '' : String(value);
        setSearchTermState(newValue);
    }, []);

    // Valor do contexto - usando useMemo para evitar recriações desnecessárias
    // mas garantindo que as referências sejam estáveis
    const value = useMemo(() => ({
        searchTerm: searchTerm || '',
        setSearchTerm,
        searchPlaceholder: searchPlaceholder || 'Buscar...',
        setSearchPlaceholder,
        onSearchChange,
        setOnSearchChange,
    }), [searchTerm, searchPlaceholder, onSearchChange, setSearchTerm]);

    return (
        <SearchContext.Provider value={value}>
            {children}
        </SearchContext.Provider>
    );
};

export const useSearch = () => {
    const context = useContext(SearchContext);
    if (!context) {
        // Retorna valores padrão se o contexto não estiver disponível
        return {
            searchTerm: '',
            setSearchTerm: () => {},
            searchPlaceholder: 'Buscar...',
            setSearchPlaceholder: () => {},
            onSearchChange: null,
            setOnSearchChange: () => {},
        };
    }
    return context;
};

